import { GroupForBookingFormQuery } from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';

export default function TMTStyle({
  group,
  spaces,
}: {
  group?: GroupForBookingFormQuery['group'];
  spaces?: number;
}) {
  let datesAndSpaces = '';

  if (group) {
    const start = DateTime.fromISO(group.start.utc)
      .setZone(group.start.zone)
      .toLocaleString(DateTime.DATE_MED);

    const end = DateTime.fromISO(group.end.utc)
      .setZone(group.end.zone)
      .toLocaleString(DateTime.DATE_MED);

    datesAndSpaces = `${start} > ${end}`;
  }

  if (spaces) {
    if (spaces > 1) {
      datesAndSpaces += ` (${spaces} spaces)`;
    } else {
      datesAndSpaces += ` (1 space)`;
    }
  }

  return (
    <style jsx global>
      {`
        /**
         * Reduce z-index to allow e.g. the header menu to be displayed
         */
        ._tmt--container,
        ._tmt--container ._tmt-container-inner,
        ._tmt--container ._tmt--modal,
        ._tmt--container ._tmt--payment-form {
          z-index: 2;
        }

        /**
         * Fix to top of the page
         */
        ._tmt--container._tmt--is-open {
          position: absolute;
          top: 57px;
          height: 100vh;
          height: calc(100vh - 57px);
          width: 100%;
          padding: 60px 0 0;
        }

        /**
         * Disable the overlay
         */
        ._tmt--container._tmt--is-open {
          background-color: transparent;
        }

        .tmt--container ._tmt--modal {
          background: transparent !important;
        }

        ._tmt--container:after {
          display: none;
        }

        /**
         * Bootstrap container emulation
         */
        ._tmt--container ._tmt-container-inner {
          display: block;
          width: 100%;
          height: 100%;
          min-height: auto;
          margin: 0;
          justify-content: auto;
        }

        @media (min-width: 576px) {
          ._tmt--container ._tmt-container-inner {
            max-width: 540px;
          }
        }

        @media (min-width: 768px) {
          ._tmt--container ._tmt-container-inner {
            max-width: 720px;
            padding-right: 12px !important;
          }
        }

        @media (min-width: 992px) {
          ._tmt--container ._tmt-container-inner {
            max-width: 960px;
            padding-right: 12px !important;
          }
        }

        @media (min-width: 1200px) {
          ._tmt--container ._tmt-container-inner {
            max-width: 1140px;
            padding-right: 12px !important;
          }
        }

        @media (min-width: 1400px) {
          ._tmt--container ._tmt-container-inner {
            max-width: 1320px;
            padding-right: 12px !important;
          }
        }

        /**
         * Bootstrap column emulation
         */
        ._tmt--container ._tmt--modal {
          position: static;
          width: 100%;
          max-width: 100%;
          height: 100%;
          padding: 0 12px;
          background-color: #fff;
        }

        @media (min-width: 768px) {
          ._tmt--container ._tmt--modal {
            width: 382px;
          }
        }

        @media (min-width: 992px) {
          ._tmt--container ._tmt--modal {
            width: 522px;
          }
        }

        @media (min-width: 1200px) {
          ._tmt--container ._tmt--modal {
            width: 627px;
          }
        }

        @media (min-width: 1400px) {
          ._tmt--container ._tmt--modal {
            width: 756px;
          }
        }

        /**
         * Rows
         */
        ._tmt--container ._tmt--row,
        ._tmt--container ._tmt--submit-container {
          margin-left: 0;
          margin-right: 0;
        }

        /**
         * Inner divs
         */
        ._tmt--container ._tmt--modal-inner {
          width: 100%;
          padding: 0;
        }

        ._tmt--modal-inner,
        ._tmt--payment-form {
          width: 100%;
          max-width: 100%;
          padding: 0;
        }

        /**
         * Back button
         */
        ._tmt--container ._tmt--close-modal {
          position: static;
          font-family: 'Poppins', sans-serif;
          color: #fff;
          background-color: #f8f9fa;
          display: inline-block;
          border: 1px solid #f8f9fa;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          font-weight: bold;
          line-height: 1.6;
          text-align: center;
          vertical-align: middle;
          padding: 10px 15px;
          font-size: 0.8rem;
          border-radius: 0.4rem;
          width: auto;
        }

        ._tmt--container ._tmt--close-modal svg {
          display: inline;
          width: 0;
          height: 1;
        }

        ._tmt--container ._tmt--close-modal:after {
          display: inline;
          content: '<Back';
          color: #333;
          z-index: 10;
        }

        ._tmt--container ._tmt--close-modal:disabled {
          display: none;
        }

        /**
         * Header
         */
        ._tmt--container ._tmt--modal-title {
          text-align: left;
          padding: 0;
          margin: 0;
        }

        ._tmt--container ._tmt--modal-title:before {
          display: block;
          content: 'Step 2 of 2: Payment';
          text-transform: uppercase;
          font-family: 'Poppins', sans-serif;
          color: #4e66f8;
          font-weight: 700;
          font-size: 16px;
          letter-spacing: 3.2px;
          padding: 15px 0 0;
          margin: 0 0 -15px;
        }

        ._tmt--container ._tmt--modal-title h3 {
          color: transparent;
          font-size: 2px;
          padding: 0;
        }

        ._tmt--container ._tmt--modal-title h3:before {
          font-family: 'Poppins', sans-serif;
          color: #333;
          font-size: 32px;
          font-weight: 700;
          text-transform: none;
          content: '${group?.trip.title || 'Card Details'}';
          display: block;
          padding: 0;
        }

        ._tmt--container ._tmt--modal-title h3:after {
          content: '${datesAndSpaces}';
          display: block;
          font-family: 'Poppins', sans-serif;
          color: #333;
          font-size: 16px;
          font-weight: 500;
          text-transform: none;
        }

        ._tmt--container ._tmt--modal-title:after {
          content: 'Card Details 🔒';
          display: block;
          font-family: 'Poppins', sans-serif;
          color: #333;
          font-size: 20px;
          font-weight: 700;
          text-transform: none;
          margin-top: -15px;
          padding: 30px 0 5px;
          filter: saturate(0);
        }

        /**
         * Currency selector
         */
        ._tmt--payment-form ._tmt--invoice label {
          display: none; /* Disable the label, which looks strange/out of place */
        }

        ._tmt--payment-form ._tmt--invoice .choices {
          display: none; /* We don't want to let the customer change their currency */
        }

        /**
         * Button
         */
        ._tmt--container ._tmt--submit-container {
          text-align: left;
        }

        ._tmt--container #_tmt--submit-button {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          background-color: #4e66f8;
          display: inline-block;
          border: 1px solid #4e66f8;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          font-weight: bold;
          line-height: 1.6;
          text-align: center;
          vertical-align: middle;
          padding: 10px 15px;
          font-size: 0.8rem;
          border-radius: 0.4rem;
          margin: 20px 0;
          width: auto;
        }

        ._tmt--container #_tmt--submit-button:focus {
          background-color: #4e66f8;
          border-color: #4e66f8;
          box-shadow: 0 0 0 0.25rem rgb(105 125 249 / 50%);
        }

        /**
         * 3D Secure
         * 
         * We make it 100% of the browser width and height (minus header) as we don't really know
         * what size it is or what it looks like.
         */
        ._tmt--modal-inner._tmt--state-iframe,
        #_tmt--verificationIframe {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          height: calc(100vh - 64px) !important;
        }

        ._tmt--submit-container::after {
          content: 'Visa and Mastercard accepted. Amex not accepted.';
          display: block;
          color: #000000;
          font-weight: 500;
          padding-bottom: 10px;
        }
      `}
    </style>
  );
}
